// frontend/src/pages/CamDoBrainDiagnostic.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import camdobrainService from '../services/camdobrainService';
import Loading from '../components/Loading';
import './CamDoBrainDiagnostic.css';

const CamDoBrainDiagnostic = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandProfileIdParam = searchParams.get('brand_profile_id');

  // State
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [error, setError] = useState(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load diagnostic when brand changes
  useEffect(() => {
    if (selectedBrandId) {
      loadDiagnostic(selectedBrandId);
    }
  }, [selectedBrandId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const profilesRes = await camdobrainService.getBrandProfiles();
      const profiles = Array.isArray(profilesRes.data)
        ? profilesRes.data
        : (profilesRes.data?.results || []);
      setBrandProfiles(profiles);

      if (brandProfileIdParam && profiles.find(p => p.id === parseInt(brandProfileIdParam))) {
        setSelectedBrandId(parseInt(brandProfileIdParam));
      } else if (profiles.length > 0) {
        setSelectedBrandId(profiles[0].id);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadDiagnostic = async (brandId) => {
    try {
      const res = await camdobrainService.getLatestDiagnostic(brandId);
      setDiagnostic(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setDiagnostic(null);
      } else {
        console.error('Error loading diagnostic:', err);
      }
    }
  };

  const handleRunDiagnostic = async () => {
    if (!selectedBrandId) return;

    try {
      setRunning(true);
      setError(null);
      const res = await camdobrainService.runDiagnostic(selectedBrandId, true);
      setDiagnostic(res.data);
    } catch (err) {
      console.error('Error running diagnostic:', err);
      setError(err.response?.data?.error || 'Error al ejecutar diagnostico');
    } finally {
      setRunning(false);
    }
  };

  const handleGenerateObjectives = async () => {
    if (!selectedBrandId || !diagnostic) return;

    try {
      setGenerating(true);
      setError(null);
      await camdobrainService.generateObjectives(selectedBrandId, diagnostic.id, true);
      navigate(`/camdobrain/objectives?brand_profile_id=${selectedBrandId}`);
    } catch (err) {
      console.error('Error generating objectives:', err);
      setError(err.response?.data?.error || 'Error al generar objetivos');
    } finally {
      setGenerating(false);
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    if (score >= 40) return '#f97316';
    return '#ef4444';
  };

  const getPhaseIcon = (phase) => {
    const icons = {
      'awareness': '🌱',
      'early_growth': '🌿',
      'scaling': '🌳',
      'maturity': '🏆',
    };
    return icons[phase] || '📊';
  };

  if (loading) {
    return <Loading />;
  }

  if (brandProfiles.length === 0) {
    return (
      <div className="diagnostic-page">
        <div className="empty-state">
          <div className="empty-icon">🧠</div>
          <h2>Sin perfiles de marca</h2>
          <p>Configura un perfil de marca primero para usar el diagnostico.</p>
          <button onClick={() => navigate('/camdobrain/setup')}>
            Configurar Marca
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="diagnostic-page">
      {/* Header */}
      <div className="diagnostic-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/camdobrain')}>
            ← Volver
          </button>
          <h1>
            <span className="icon">🔬</span>
            Diagnostico de Cuenta
          </h1>
        </div>
        <div className="header-actions">
          <select
            value={selectedBrandId || ''}
            onChange={(e) => setSelectedBrandId(Number(e.target.value))}
            className="brand-select"
          >
            {brandProfiles.map(brand => (
              <option key={brand.id} value={brand.id}>
                {brand.brand_name} (@{brand.account_username})
              </option>
            ))}
          </select>
          <button
            className="run-btn"
            onClick={handleRunDiagnostic}
            disabled={running || !selectedBrandId}
          >
            {running ? '⏳ Analizando...' : '🔄 Nuevo Diagnostico'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {/* No diagnostic yet */}
      {!diagnostic && !running && (
        <div className="no-diagnostic">
          <div className="icon">📊</div>
          <h2>Sin diagnostico</h2>
          <p>Ejecuta un diagnostico para analizar el estado de tu cuenta de Instagram.</p>
          <button onClick={handleRunDiagnostic} disabled={running}>
            {running ? 'Analizando...' : 'Ejecutar Diagnostico'}
          </button>
        </div>
      )}

      {/* Running state */}
      {running && (
        <div className="running-state">
          <Loading fullScreen={false} mensaje="Analizando cuenta" />
          <p>Esto puede tomar unos segundos. Estamos recolectando metricas y generando insights con IA.</p>
        </div>
      )}

      {/* Diagnostic Results */}
      {diagnostic && !running && (
        <div className="diagnostic-content">
          {/* Health Score & Phase */}
          <div className="top-cards">
            {/* Health Score Card */}
            <div className="health-score-card">
              <h3>Health Score</h3>
              <div className="score-gauge">
                <svg viewBox="0 0 200 120" className="gauge-svg">
                  <path
                    d="M 20 100 A 80 80 0 0 1 180 100"
                    fill="none"
                    stroke="#e2e8f0"
                    strokeWidth="12"
                    strokeLinecap="round"
                  />
                  <path
                    d="M 20 100 A 80 80 0 0 1 180 100"
                    fill="none"
                    stroke={getHealthScoreColor(diagnostic.health_score)}
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${(diagnostic.health_score / 100) * 251} 251`}
                  />
                </svg>
                <div className="score-value" style={{ color: getHealthScoreColor(diagnostic.health_score) }}>
                  {Math.round(diagnostic.health_score)}
                </div>
                <div className="score-label">de 100</div>
              </div>
              <div className="score-status">
                {diagnostic.health_score >= 80 ? 'Excelente' :
                 diagnostic.health_score >= 60 ? 'Bueno' :
                 diagnostic.health_score >= 40 ? 'Regular' : 'Necesita mejoras'}
              </div>
            </div>

            {/* Growth Phase Card */}
            <div className="phase-card">
              <h3>Fase de Crecimiento</h3>
              <div className="phase-badge">
                <span className="phase-icon">{getPhaseIcon(diagnostic.growth_phase)}</span>
                <span className="phase-name">{diagnostic.growth_phase_display}</span>
              </div>
              <p className="phase-reasoning">{diagnostic.growth_phase_reasoning}</p>
              <div className="phase-metrics">
                <div className="metric">
                  <span className="label">Seguidores</span>
                  <span className="value">{diagnostic.snapshot_metrics?.followers?.toLocaleString() || 0}</span>
                </div>
                <div className="metric">
                  <span className="label">ER</span>
                  <span className="value">{diagnostic.snapshot_metrics?.avg_engagement_rate || 0}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Score Breakdown */}
          <div className="breakdown-card">
            <h3>Desglose del Score</h3>
            <div className="breakdown-bars">
              {Object.entries(diagnostic.health_score_breakdown || {}).map(([key, data]) => (
                <div key={key} className="breakdown-item">
                  <div className="breakdown-header">
                    <span className="breakdown-name">
                      {key === 'engagement' ? 'Engagement' :
                       key === 'reach' ? 'Alcance' :
                       key === 'growth' ? 'Crecimiento' :
                       key === 'consistency' ? 'Consistencia' :
                       key === 'content_quality' ? 'Calidad de Contenido' : key}
                    </span>
                    <span className="breakdown-score">{Math.round(data.score)}/100</span>
                  </div>
                  <div className="breakdown-bar">
                    <div
                      className="breakdown-fill"
                      style={{
                        width: `${data.score}%`,
                        backgroundColor: getHealthScoreColor(data.score)
                      }}
                    ></div>
                  </div>
                  <div className="breakdown-detail">
                    Actual: {typeof data.current === 'number' ? data.current.toFixed(1) : data.current} |
                    Objetivo: {data.target}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SWOT Analysis */}
          <div className="swot-grid">
            <div className="swot-card strengths">
              <h4>💪 Fortalezas</h4>
              <ul>
                {(diagnostic.strengths || []).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
                {(!diagnostic.strengths || diagnostic.strengths.length === 0) && (
                  <li className="empty">Sin datos</li>
                )}
              </ul>
            </div>
            <div className="swot-card weaknesses">
              <h4>⚠️ Debilidades</h4>
              <ul>
                {(diagnostic.weaknesses || []).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
                {(!diagnostic.weaknesses || diagnostic.weaknesses.length === 0) && (
                  <li className="empty">Sin datos</li>
                )}
              </ul>
            </div>
            <div className="swot-card opportunities">
              <h4>🚀 Oportunidades</h4>
              <ul>
                {(diagnostic.opportunities || []).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
                {(!diagnostic.opportunities || diagnostic.opportunities.length === 0) && (
                  <li className="empty">Sin datos</li>
                )}
              </ul>
            </div>
            <div className="swot-card threats">
              <h4>🔥 Amenazas</h4>
              <ul>
                {(diagnostic.threats || []).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
                {(!diagnostic.threats || diagnostic.threats.length === 0) && (
                  <li className="empty">Sin datos</li>
                )}
              </ul>
            </div>
          </div>

          {/* Competitor Benchmark */}
          {diagnostic.competitor_benchmark?.competitors?.length > 0 && (
            <div className="benchmark-card">
              <h3>📊 Benchmark vs Competencia</h3>
              {diagnostic.competitor_benchmark.position && (
                <div className="position-badge">
                  Posicion #{diagnostic.competitor_benchmark.position.rank} de {diagnostic.competitor_benchmark.position.total}
                  <span className="percentile">
                    (Top {100 - diagnostic.competitor_benchmark.position.percentile}%)
                  </span>
                </div>
              )}
              <table className="benchmark-table">
                <thead>
                  <tr>
                    <th>Cuenta</th>
                    <th>Seguidores</th>
                    <th>Engagement Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="own-row">
                    <td>Tu cuenta</td>
                    <td>{diagnostic.snapshot_metrics?.followers?.toLocaleString()}</td>
                    <td>{diagnostic.snapshot_metrics?.avg_engagement_rate}%</td>
                  </tr>
                  {diagnostic.competitor_benchmark.competitors.map((comp, i) => (
                    <tr key={i}>
                      <td>@{comp.username}</td>
                      <td>{comp.followers?.toLocaleString()}</td>
                      <td>{comp.avg_er}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* AI Analysis */}
          {diagnostic.ai_analysis && Object.keys(diagnostic.ai_analysis).length > 0 && (
            <div className="ai-analysis-card">
              <h3>🤖 Analisis de IA</h3>
              {diagnostic.ai_analysis.summary && (
                <div className="ai-summary">
                  <p>{diagnostic.ai_analysis.summary}</p>
                </div>
              )}

              {diagnostic.ai_analysis.key_insights?.length > 0 && (
                <div className="ai-section">
                  <h4>Insights Clave</h4>
                  <ul>
                    {diagnostic.ai_analysis.key_insights.map((insight, i) => (
                      <li key={i}>{insight}</li>
                    ))}
                  </ul>
                </div>
              )}

              {diagnostic.ai_analysis.immediate_actions?.length > 0 && (
                <div className="ai-section">
                  <h4>Acciones Inmediatas</h4>
                  <ul>
                    {diagnostic.ai_analysis.immediate_actions.map((action, i) => (
                      <li key={i}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}

              {diagnostic.ai_analysis.content_recommendations?.length > 0 && (
                <div className="ai-section">
                  <h4>Recomendaciones de Contenido</h4>
                  <ul>
                    {diagnostic.ai_analysis.content_recommendations.map((rec, i) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {diagnostic.ai_analysis.growth_potential && (
                <div className="growth-potential">
                  <span className="label">Potencial de Crecimiento:</span>
                  <span className={`badge ${diagnostic.ai_analysis.growth_potential}`}>
                    {diagnostic.ai_analysis.growth_potential === 'alto' ? '🚀 Alto' :
                     diagnostic.ai_analysis.growth_potential === 'medio' ? '📈 Medio' : '📊 Bajo'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Generate Objectives CTA */}
          <div className="objectives-cta">
            <div className="cta-content">
              <h3>🎯 Genera Objetivos Estrategicos</h3>
              <p>Basado en este diagnostico, podemos generar objetivos personalizados con tacticas y KPIs.</p>
            </div>
            <button
              className="generate-btn"
              onClick={handleGenerateObjectives}
              disabled={generating}
            >
              {generating ? '⏳ Generando...' : '✨ Generar Objetivos'}
            </button>
          </div>

          {/* Diagnostic Info */}
          <div className="diagnostic-info">
            <span>Diagnostico generado: {new Date(diagnostic.created_at).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CamDoBrainDiagnostic;
